/* ==========================================================================
   A cleaner's profile -- the same component whether you are looking at your
   own or at someone you might book. `isMe` is the only difference.
   ========================================================================== */

import { useState } from 'react';
import type { Cleaner } from '../../types';
import { Icon } from '../../components/Icon';
import { TranslateToggle, useTranslated } from '../../components/Translatable';
import {
  Avatar, Card, Divider, DimBar, Empty, JobChips, Note, SectionHead, Slider, Stars, Stepper,
  Switch, Seg, Pick, Field,
} from '../../components/ui';
import { ProfilePhoto } from '../../components/Photos';
import { SigninCode } from './Misc';
import { CLEANER_RATING_DIMS, JOB_CATALOG, LANGUAGES, TRAITS } from '../../data/catalog';
import { AVAILABILITY, REQUESTS, REVIEWS_OF_CLEANERS } from '../../data/seed';
import { LANG_LEVEL } from '../../data/catalog';
import { ZipField } from '../../components/ZipField';
import { TownPicks } from '../../components/TownPicks';
import {
  areaLabel, cleaner, dayState, jobLabel, lang, nextFreeDate, trait, shortName, dimLabel,
} from '../../lib/domain';
import { TODAY_ISO, fmtDate, fmtTime, money } from '../../lib/time';
import { t, tf } from '../../lib/i18n';
import {
  activeCleaner, activeHouse, commit, inviteToRequest, nav, openChat, openSheet, saveCleaner,
  shareLink, startEditCleaner, state, tab, toast, toggleHidden, toggleIn,
} from '../../store';

export function MyCleanerProfile() {
  const c = activeCleaner();
  /* Cleaner-only screen. A home reaching it means a bad link, not a crash. */
  if (!c) return <div className="screen-pad"><Empty big={t('c_cleaneronly')} /></div>;

  return <Profile c={c} isMe />;
}
export function OtherCleanerProfile() { return <Profile c={cleaner(state.params.id!)} />; }

function Profile({ c, isMe }: { c: Cleaner; isMe?: boolean }) {
  const revs = REVIEWS_OF_CLEANERS[c.id] || [];
  const av = AVAILABILITY[c.id];
  /* Only a home has a home. A cleaner looking at their own profile has no
     house context at all, so everything derived from one is optional. */
  const h = state.role === 'house' ? activeHouse() : undefined;
  const free = isMe ? null : nextFreeDate(c.id, TODAY_ISO, 3);
  const noShared = !!h && !c.languages.some((cl) => h.languages.some((hl) => hl.id === cl.id));
  const ctxReq = state.ctx;

  /* The jobs this home could invite somebody to: its own, still open, and not
     already in the past. Needed because a cleaner's profile is reached from
     Find as often as from inside a job, and from Find there is no job in
     hand. */
  const myOpen = h
    ? REQUESTS.filter((r) => r.houseId === h.id && r.status === 'open' && r.date >= TODAY_ISO)
      .sort((a, b) => (a.date < b.date ? -1 : 1))
    : [];
  const [choosing, setChoosing] = useState(false);
  /* Where she already stands on the job an invite would go to. The button
     used to stay live and answer only after a tap ("has already applied"). */
  const target = ctxReq ?? (myOpen.length === 1 ? myOpen[0].id : null);
  const standing = target
    ? REQUESTS.find((r) => r.id === target)?.applicants.find((a) => a.cleanerId === c.id)
    : undefined;

  const tr = useTranslated([c.bio, ...revs.map((rv) => rv.text)]);
  const [bioT, ...reviewsT] = tr.view;

  return (
    <div className="screen-pad formgrid">
      <Card>
        <div className="row top">
          <Avatar p={c} size="lg" />
          <div className="col grow">
            <b className="name" style={{ fontSize: 19 }}>{c.name}</b>
            <span className="sub">{c.headline}</span>
            <div className="row" style={{ gap: 7, marginTop: 5 }}>
              <Stars value={c.ratings.overall} big />
              <span className="sub tight">{c.ratings.count} {t('reviews')}</span>
            </div>
          </div>
        </div>
        <div className="chips">
          <span className="chip tile">{money(c.rate)}{t('perhour')}</span>
          <span className="chip">{c.minHours}h {t('minimum')}</span>
          <span className="chip">{c.yearsExp} {t('years')}</span>
          <span className="chip">{c.gender === 'female' ? t('tx_woman') : t('tx_man')}</span>
          {c.team && <span className="chip warm">{tf('tx_team_of', { n: c.team })}</span>}
          <span className="chip good">{tf('tx_repeat_homes', { n: c.repeatClients })}</span>
        </div>
        <span className="sub">{bioT}</span>
        <TranslateToggle
          showing={tr.showing}
          busy={tr.busy}
          failed={tr.failed}
            changed={tr.changed}
          onToggle={tr.toggle}
        />
        {free && (
          <div className="freewin">
            <Icon name="clock" /> {t('tx_next_avail')} {fmtDate(free, { long: true })} ·{' '}
            {dayState(c.id, free).windows
              .map((w) => fmtTime(w.from) + '–' + fmtTime(w.to))
              .join(', ')}
          </div>
        )}
      </Card>

      {isMe && av && (
        <Card className={av.hidden ? 'warm' : ''}>
          <div className="switchrow">
            <div className="col">
              <b style={{ fontSize: 14 }}>{t('c_showme')}</b>
              <span className="hint">
                {av.hidden
                  ? t('tx_hidden')
                  : t('tx_findable_avail')}
              </span>
            </div>
            <Switch on={!av.hidden} onClick={toggleHidden} />
          </div>
        </Card>
      )}

      <Card>
        <SectionHead title={t('languages')} />
        <div className="chips">
          {c.languages.map((l) => (
            <span className={'chip' + (l.level === 'basic' ? '' : ' tile')} key={l.id}>
              {lang(l.id)} · {LANG_LEVEL[l.level]}
            </span>
          ))}
        </div>
        {!isMe && noShared && (
          <Note warm>
            {t('tx_no_common_lang')}
          </Note>
        )}
      </Card>

      <Card>
        <SectionHead title={t('specialties')} />
        <div className="chips"><JobChips ids={c.specialties} /></div>
        <Divider />
        <div className="chips">
          {c.traits.map((x) => <span className="chip outline" key={x}>{trait(x)}</span>)}
        </div>
      </Card>

      <Card>
        <SectionHead title={t('areas')} />
        <div className="chips">
          {c.areas.map((a) => (
            <span className={'chip' + (h && a === h.area ? ' good' : '')} key={a}>{areaLabel(a)}</span>
          ))}
        </div>
      </Card>

      <Card>
        <SectionHead title={t('h_howtheyrated')} />
        {CLEANER_RATING_DIMS.map((d) => (
          <DimBar key={d.id} label={dimLabel(d)} value={(c.ratings as any)[d.id]} />
        ))}
      </Card>

      <Card>
        <SectionHead title={t('h_reliability')} />
        <div className="row between">
          <span className="sub">{t('cp_replytime')}</span>
          {/* Nobody has measured a new cleaner's replies yet; "1 hr" was the
              default, shown as if it were a record. */}
          <b className="stat">
            {c.ratings.count === 0 && c.repeatClients === 0
              ? t('tx_new_here')
              : c.responseMins < 60 ? c.responseMins + ' min' : Math.round(c.responseMins / 60) + ' hr'}
          </b>
        </div>
        <div className="row between">
          <span className="sub">{t('cp_calconfirmed')}</span>
          <b className="stat">{av ? fmtDate(av.lastConfirmed) : '—'}</b>
        </div>
        <div className="row between">
          <span className="sub">{t('cp_phoneverified')}</span>
          <b>{c.verified.phone ? '✓ Yes' : 'No'}</b>
        </div>
        <div className="row between">
          <span className="sub">{t('cp_references')}</span>
          <b className="stat">{c.verified.references}</b>
        </div>
        {c.reliabilityFlags > 0 && (
          <Note warm>
            <b>{tf(c.reliabilityFlags > 1 ? 'tx_cal_reports_n' : 'tx_cal_reports_1', { n: c.reliabilityFlags })}</b>{' '}
            {t('tx_cal_reports_more')}
          </Note>
        )}
      </Card>

      {revs.length > 0 && (
        <Card>
          <SectionHead title={t('reviews')} action={tf('tx_total', { n: revs.length })} />
          {revs.map((rv, i) => (
            <div key={i}>
              {i > 0 && <Divider />}
              <div className="col" style={{ gap: 5 }}>
                <div className="row between">
                  <span className="row" style={{ gap: 7 }}>
                    <Avatar p={rv.by} size="sm" />
                    <b style={{ fontSize: 13.5 }}>{rv.by.name}</b>
                  </span>
                  <Stars value={rv.overall} />
                </div>
                <span className="sub">&quot;{reviewsT[i] ?? rv.text}&quot;</span>
                <div className="chips">
                  <JobChips ids={rv.jobIds} max={3} />
                  <span className="chip outline">{fmtDate(rv.date, { long: true })}</span>
                </div>
              </div>
            </div>
          ))}
        </Card>
      )}

      {isMe ? (
        <>
          <button type="button" className="btn block" onClick={startEditCleaner}>
            {t('t_editprofile')}
          </button>
          <button type="button" className="btn ghost block" onClick={() => tab('c_calendar')}>
            {t('t_mycalendar')}
          </button>
          {/* A cleaner's own profile is the thing they hand to a new home. */}
          <button
            type="button"
            className="btn ghost block"
            onClick={() => void shareLink('cleaner')}
          >
            {t('cp_shareprofile')}
          </button>
          {/* The way back in from another phone, on the screen people look at. */}
          {isMe && !state.me?.isDemo && (
            <Card>
              <SectionHead title={t('h_otherdevice')} />
              <SigninCode />
            </Card>
          )}

          <button type="button" className="btn ghost block" onClick={() => nav('settings')}>
            {t('t_settings')}
          </button>
          <button type="button" className="btn quiet block" onClick={() => nav('feedback')}>
            {t('t_feedback')}
          </button>
        </>
      ) : (
        <>
          {standing && standing.status !== 'declined' ? (
            <button
              type="button"
              className="btn block lg"
              onClick={() => nav('h_request', { id: target! })}
            >
              {standing.status === 'invited'
                ? t('tx_invited_see')
                : tf('tx_applied_see', { name: shortName(c.name) })}
            </button>
          ) : (
          <button
            type="button"
            className="btn block lg"
            onClick={() => {
              /* This used to say "Invitation sent" whenever there was no job
                 in hand -- and send nothing. Oster invited a cleaner from Find,
                 was told it had worked, and the cleaner never saw a thing,
                 because no request was ever made. A toast that claims success
                 for something that did not happen is worse than an error.

                 Now: the job in hand if there is one; this home's only open
                 job if there is exactly one; a choice if there are several;
                 and if there are none, the honest answer, which is that there
                 is nothing to invite anybody to yet. */
              if (ctxReq) { inviteToRequest(ctxReq, c.id); return; }
              if (myOpen.length === 1) { inviteToRequest(myOpen[0].id, c.id); return; }
              if (myOpen.length > 1) { setChoosing(true); return; }
              toast(tf('tx_post_first', { name: shortName(c.name) }));
              nav('h_post');
            }}
          >
            {t('b_invite')} {shortName(c.name)}
          </button>
          )}
          {choosing && (
            <div className="col" style={{ gap: 8 }}>
              <span className="hint">{t('tx_which_job')}</span>
              {myOpen.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="btn ghost block"
                  onClick={() => { setChoosing(false); inviteToRequest(r.id, c.id); }}
                >
                  {fmtDate(r.date, { long: true })} · {fmtTime(r.start)}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="btn ghost block"
            onClick={() => openChat({ cleanerId: c.id })}
          >
            {t('b_message')}
          </button>
          <button
            type="button"
            className="btn quiet block"
            onClick={() => openSheet({ name: 'report', id: c.id })}
          >
            {t('cp_reportcal')}
          </button>
          <button
            type="button"
            className="btn quiet block"
            onClick={() => void shareLink('cleaner', c.id)}
          >
            {tf('tx_recommend', { name: shortName(c.name) })}
          </button>
          <button
            type="button"
            className="btn quiet block"
            onClick={() =>
              openSheet({ name: 'hide', id: c.id, who: 'cleaner', name2: shortName(c.name) })
            }
          >
            {tf('tx_dont_show', { name: shortName(c.name) })}
          </button>
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- editing */

export function EditCleanerProfile() {
  const e = state.edit;
  /* Saving clears the draft and then goes back, and going back waits for
     the browser -- so for a moment this screen is drawn with no draft. That
     was a crash on every save; now it is a blank moment. */
  if (!e) return null;
  const groups = [...new Set(JOB_CATALOG.map((j) => j.group))];
  const set = (k: string, v: unknown) => { e[k] = v; commit(); };
  const step = (k: string, d: number) => { e[k] = Math.max(0, e[k] + d); commit(); };
  const flip = (k: string, v: string) => { toggleIn(e[k], v); commit(); };

  const me = activeCleaner();

  return (
    <div className="screen-pad formgrid">
      {me && (
        <Card>
          <SectionHead title={t('cp_yourpicture')} />
          {/* The only photo a cleaner can add anywhere in the app. */}
          <ProfilePhoto
            kind="profile-cleaner"
            ownerId={me.id}
            current={me.photoId}
            onChanged={(id) => { me.photoId = id; commit(); }}
          />
        </Card>
      )}

      <Card>
        <SectionHead title={t('h_aboutyou')} />
        <Field label={t('tx_name')}>
          <input className="input" value={e.name} onChange={(ev) => set('name', ev.target.value)} />
        </Field>
        <Field label={t('cp_oneline')}>
          <input
            className="input"
            value={e.headline}
            onChange={(ev) => set('headline', ev.target.value)}
          />
        </Field>
        <Field label={t('cp_longer')}>
          <textarea
            className="textarea"
            value={e.bio}
            onChange={(ev) => set('bio', ev.target.value)}
          />
        </Field>
        <Field label={t('cp_youare')}>
          <Seg
            options={[{ id: 'female', label: t('tx_a_woman') }, { id: 'male', label: t('tx_a_man') }]}
            value={e.gender}
            onChange={(v) => set('gender', v)}
          />
        </Field>
      </Card>

      <Card>
        <SectionHead title={t('h_rateandhours')} />
        <Field label={t('pr_rateperhour') + money(e.rate)}>
          {/* min mirrors RATE_FLOOR on the server, which is what actually
              enforces it. Kept explicit rather than relying on the Slider's
              own default, so changing the floor is one obvious place. */}
          <Slider min={15} max={200} value={e.rate} onChange={(v) => set('rate', v)} />
        </Field>
        <Field label={t('cp_minhours')}>
          <Stepper value={e.minHours} onStep={(d) => step('minHours', d)} />
        </Field>
        <Field label={t('cp_years')}>
          <Stepper value={e.yearsExp} onStep={(d) => step('yearsExp', d)} />
        </Field>
      </Card>

      <Card>
        <SectionHead title={t('h_where')} />
        <ZipField
          label={t('cp_startfrom')}
          hint={t('tx_zip_hint')}
          value={e.baseZip ?? ''}
          onChange={(z) => set('baseZip', z)}
        />
        <Field
          label={t('cp_howfar') + tf('tx_n_miles', { n: e.radiusMiles ?? 20 })}
          hint={t('tx_further_hint')}
        >
          <div className="slider">
            <input
              type="range"
              min={5}
              max={100}
              step={5}
              value={e.radiusMiles ?? 20}
              onChange={(ev) => set('radiusMiles', Number(ev.target.value))}
            />
            <div className="ends"><span>5 mi</span><span>100 mi</span></div>
          </div>
        </Field>
      </Card>

      <Card>
        <SectionHead title={t('areas')} />
        <span className="hint">
          {t('tx_areas_hint')}
        </span>
        <TownPicks
          zip={e.baseZip ?? ''}
          anchors={e.areas}
          chosen={e.areas}
          onFlip={(id) => flip('areas', id)}
          /* How far she said she travels, not a fixed 30. Raising it used to
             change nothing here, so a cleaner willing to go 40 miles still
             could not offer a town 32 miles away. */
          within={e.radiusMiles ?? 20}
        />
      </Card>

      <Card>
        <SectionHead title={t('languages')} />
        <div className="chips">
          {LANGUAGES.map((l) => (
            <Pick key={l.id} label={l.label} on={e.langIds.includes(l.id)}
              onClick={() => flip('langIds', l.id)} />
          ))}
        </div>
      </Card>

      <Card>
        <SectionHead title={t('h_howyouwork')} />
        <div className="chips">
          {TRAITS.map((x) => (
            <Pick key={x.id} label={x.label} on={e.traits.includes(x.id)}
              onClick={() => flip('traits', x.id)} />
          ))}
        </div>
      </Card>

      {groups.map((g) => (
        <Card key={g}>
          <SectionHead title={t('specialties') + ' — ' + g} />
          <div className="chips">
            {JOB_CATALOG.filter((j) => j.group === g).map((j) => (
              <Pick key={j.id} label={jobLabel(j.id)} on={e.specialties.includes(j.id)}
                onClick={() => flip('specialties', j.id)} />
            ))}
          </div>
        </Card>
      ))}

      <button type="button" className="btn block lg" onClick={saveCleaner}>
        {t('b_saveprofile')}
      </button>
    </div>
  );
}
